import { AgGridTable } from 'components/AgGrid/AgGridTable';
import { ModalDialog } from 'components/ModalDialog';
import type { PriceListUerDismantlingRatioData } from 'gql/graphql';
import { DismantlingCoefficientTypeEnum } from 'gql/graphql';
import { IconWarning, Informer, Tabs } from 'uikit';

import type React from 'react';
import { useCallback, useState, useMemo } from 'react';

import { UerRatiosColumns } from '../hooks/uerRatiosColumns';
import { useUerDataTableRatios } from '../hooks/useUerDataTableRatios';
import { useUerPriceListUerForDismantlingRatio } from '../hooks/useUerPriceListUerForDismantlingRatio';
import { useUerPriceListUerForDismantlingRatioColumns } from '../hooks/useUerPriceListUerForDismantlingRatioColumns';

import Style from './UerSelectionModal.module.css';

export interface IrowGroupTitleItem {
    isGroup: boolean;
    ratioCode: string;
    rowCount: number;
}
interface IUerSelectionModal {
    isReadOnly: boolean;
    contractId: number;
    groupType: DismantlingCoefficientTypeEnum;
    onClose: (isConfirm?: boolean) => void;
    onCheckChange: (checkedIds: number[]) => void;
    checkedIds?: number[];
    title: string;
    uerId: number;
}

enum TabItemsIdsEnum {
    coefficients = 'ratios',
    prices = 'uers',
}

const tabItems = [
    {
        id: TabItemsIdsEnum.coefficients,
        label: 'Коэффициенты',
    },
    {
        id: TabItemsIdsEnum.prices,
        label: 'Расценки',
    },
];

const getRowId = ({ data }: { data: PriceListUerDismantlingRatioData }) => data?.uerId;

export const UerSelectionModal: React.FC<IUerSelectionModal> = (props) => {
    const { isReadOnly, contractId, groupType, onClose, checkedIds, onCheckChange, title, uerId } = props;
    const [filterValues, setFilterValues] = useState<Record<string, string[]>>({});
    const [groupStates, setGroupStates] = useState<Record<string, boolean>>({});

    const isEquipmentInstallGroupType = useMemo(
        () => groupType === DismantlingCoefficientTypeEnum.EquipmentInstallation,
        [groupType],
    );

    const [selectedTab, setSelectedTab] = useState(isEquipmentInstallGroupType ? tabItems[1] : tabItems[0]);

    const isRatiosGridShow = useMemo(
        () => selectedTab.id === TabItemsIdsEnum.coefficients && selectedTab === tabItems[0],
        [selectedTab],
    );

    const dataVariables = useMemo(() => {
        if (!isEquipmentInstallGroupType && selectedTab.id === TabItemsIdsEnum.coefficients) {
            return {
                contractId,
                preselectedUerBaseRowIds: [uerId],
                groupType,
            };
        }

        if (!isEquipmentInstallGroupType && selectedTab.id === TabItemsIdsEnum.prices) {
            return {
                contractId,
                groupType: null,
                preselectedUerBaseRowIds: [uerId],
            };
        }

        return {
            contractId,
            groupType,
            preselectedUerBaseRowIds: [uerId],
        };
    }, [contractId, groupType, isEquipmentInstallGroupType, selectedTab.id, uerId]);

    const uerDataSource = useUerPriceListUerForDismantlingRatio({
        filterValues,
        dataVariables,
    });

    const uerColumns = useUerPriceListUerForDismantlingRatioColumns({
        isReadOnly,
        onCheckChange,
        checkedIds,
        setFilterValues,
        groupType: dataVariables?.groupType,
        contractId,
        uerId,
    });

    const { rowData, itemsData } = useUerDataTableRatios({
        filterValues,
        dataVariables,
        groupStates,
        isRatiosGridShow,
    });

    const ratiosColumns = UerRatiosColumns({
        onCheckChange,
        checkedIds,
        setFilterValues,
        groupType,
        contractId,
        uerId,
        groupStates,
        setGroupStates,
        itemsData,
    });

    const onTabChange = useCallback(({ value }) => setSelectedTab(value), []);

    const onConfirm = useCallback(() => {
        onClose(true);
    }, [onClose]);

    return (
        <ModalDialog
            isOpen
            title={title}
            onConfirm={onConfirm}
            onClose={onClose}
            withTitleCloseButton
            btnConfirmLabel={isReadOnly ? 'Закрыть' : 'Добавить'}
            btnCancelLabel={isReadOnly ? undefined : 'Отменить'}
            style={{ width: 1280 }}
        >
            <div
                className={
                    isEquipmentInstallGroupType
                        ? Style.equipmentInstallationModalBody
                        : Style.constructionWorksModalBody
                }
            >
                {!isEquipmentInstallGroupType && <Tabs items={tabItems} onChange={onTabChange} value={selectedTab} />}
                <div
                    className={
                        isEquipmentInstallGroupType
                            ? Style.equipmentInstallationTableContainer
                            : Style.constructionWorksTableContainer
                    }
                >
                    {selectedTab === tabItems[0] ||
                        (selectedTab === tabItems[1] && selectedTab.id === TabItemsIdsEnum.prices && (
                            <AgGridTable
                                columnDefs={uerColumns}
                                datasource={uerDataSource}
                                rowModelType="infinite"
                                maxConcurrentDatasourceRequests={1}
                                maxBlocksInCache={50}
                                cacheBlockSize={50}
                                getRowId={getRowId}
                            />
                        ))}
                    {isRatiosGridShow && (
                        <AgGridTable
                            rowClassRules={{
                                groupRow: ({ data }) => !!(data as IrowGroupTitleItem)?.isGroup,
                                defaultRowClass: ({ data }) => !(data as IrowGroupTitleItem)?.isGroup,
                            }}
                            rowData={rowData}
                            columnDefs={ratiosColumns}
                            rowSelection="multiple"
                        />
                    )}
                </div>
                {!isEquipmentInstallGroupType && (
                    <Informer
                        className={Style.informer}
                        status="system"
                        label="Для формирования области применения создаваемого коэффициента добавьте расценки и/или выберите существующие области применения других коэффициентов"
                        icon={IconWarning}
                    />
                )}
            </div>
        </ModalDialog>
    );
};

UerSelectionModal.displayName = 'UerSelectionModal';
